// Gedeelde divergentie-detectie: vergelijkt vastgelegdeWeken met maandroosters.
// ⚠️ Dit is een BEWUSTE KOPIE van divergerendeCellen in server/src/index.js.
// De server mag NIET uit client/src/lib/ importeren — de deploy-zip bevat alleen
// engine/ en data/, dus zo'n import crasht de Azure-server. Wijzig je de ene,
// wijzig dan ook de andere, en draai daarna:
//   node client/test/diag/divergentie-server-vs-script.mjs

// ── consistentiecheck: lopen vastgelegdeWeken en maandroosters uit elkaar? ───────────
// Geeft de afwijkende CELLEN terug (leeg = consistent).
//
// vastgelegdeWeken[`${isoJaar}-${weeknummer}`].week.rooster = wat de server serveert
// (TeamRooster + Mijn Rooster). maandroosters[`${jaar}-${maand}`].weken[i].rooster = wat de
// client-matrix toont. Voor elke vastgelegde week horen die twee identiek te zijn; liepen ze
// uiteen, dan zag de ene medewerker een dienst die de ander niet zag (voorval 2026-08-05,
// 10 cellen in arnhem/late_dienst_dw).
//
// BEWUST een 1-op-1 overname van client/test/diag/vastgelegd-vs-maandroosters.mjs:59-110 —
// dezelfde koppeling, dezelfde celsleutels, dezelfde afwijk-toets. Niet slimmer maken dan dat
// script: dat is de meetlat waarmee de correctie voor én na is geverifieerd, en twee
// verschillende definities van "afwijkend" maken die meting waardeloos.
//
// Twee eigenschappen die je van het script erft en moet kennen:
//  - VOLGORDEGEVOELIG: de toets is JSON.stringify op de namen-array, dus dezelfde personen in
//    een andere volgorde telt als afwijkend. Dat is gewenst — wijzigCel plakt een toevoeging
//    achteraan terwijl de engine op zijn eigen volgorde uitkomt, en dat verschil wil je zien.
//  - ASYMMETRISCH: itereert over vastgelegdeWeken, dus een week die ALLEEN in maandroosters
//    staat wordt niet gezien. Een vastgelegde week zonder tegenhanger evenmin.
export function divergerendeCellen(blok) {
  const uit = [];
  const vast = blok?.vastgelegdeWeken;
  const mrs = blok?.maandroosters;
  if (!vast || typeof vast !== 'object' || !mrs || typeof mrs !== 'object') return uit;
  const DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo']; // alleen voor een stabiele volgorde

  for (const entry of Object.values(vast)) {
    const vRooster = entry?.week?.rooster;
    if (!vRooster || typeof vRooster !== 'object') continue;   // oud formaat / boolean → overslaan

    // Zoek dezelfde week terug over ALLE maanden; match op weeknummer + isoJaar, met beide
    // kanten door `?? null` zodat undefined en null op elkaar matchen.
    const treffers = [];
    for (const mr of Object.values(mrs)) {
      for (const w of (mr?.weken || [])) {
        if (w?.weeknummer === entry.weeknummer && (w?.isoJaar ?? null) === (entry.isoJaar ?? null)) {
          treffers.push(w);
        }
      }
    }
    if (treffers.length === 0) continue;                       // geen tegenhanger → niets te vergelijken

    for (const t of treffers) {
      const mRooster = t.rooster || {};
      // Celsleutels uit BEIDE roosters, zodat een cel die aan één kant ontbreekt ook opvalt.
      const sleutels = new Set();
      for (const bron of [vRooster, mRooster]) {
        for (const [gem, diensten] of Object.entries(bron)) {
          for (const [dienstId, dagen] of Object.entries(diensten || {})) {
            for (const dag of Object.keys(dagen || {})) sleutels.add(`${gem}|${dienstId}|${dag}`);
          }
        }
      }
      // Sorteren zoals het script: gemeente, dienst, dag-volgorde. Nodig omdat de aanroeper
      // bij veel afwijkingen de EERSTE N logt — zonder vaste volgorde zou die selectie per
      // request kunnen verschillen.
      const gesorteerd = [...sleutels].sort((a, b) => {
        const [ga, da, dga] = a.split('|'); const [gb, db, dgb] = b.split('|');
        return ga.localeCompare(gb) || da.localeCompare(db) || DAGEN.indexOf(dga) - DAGEN.indexOf(dgb);
      });

      for (const s of gesorteerd) {
        const [gem, dienstId, dag] = s.split('|');
        const a = vRooster?.[gem]?.[dienstId]?.[dag];
        const c = mRooster?.[gem]?.[dienstId]?.[dag];
        const aL = Array.isArray(a) ? a : null;
        const cL = Array.isArray(c) ? c : null;
        if (JSON.stringify(aL) === JSON.stringify(cL)) continue;
        uit.push({
          isoJaar: entry.isoJaar ?? null,
          weeknummer: entry.weeknummer ?? null,
          gemeenteKey: gem,
          dienstId,
          dag,
          alleenVast: (aL || []).filter((n) => !(cL || []).includes(n)),
          alleenMatrix: (cL || []).filter((n) => !(aL || []).includes(n)),
        });
      }
    }
  }
  return uit;
}

// Alleen ECHTE personeelsverschillen — een cel waarin dezelfde namen in een
// andere volgorde staan telt hier NIET mee. Voor het serverlogboek is de
// volgordegevoeligheid gewenst (zie het commentaar hierboven); voor de
// teamleider-banner niet: het team ziet dan feitelijk hetzelfde rooster en
// een waarschuwing zonder betekenis kost vertrouwen in de echte.
export function echteAfwijkingen(blok) {
  return divergerendeCellen(blok).filter(
    (c) => (c.alleenVast?.length || 0) > 0 || (c.alleenMatrix?.length || 0) > 0
  );
}
